import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ClipboardPaste,
  Copy,
  Crosshair,
  EyeOff,
  FolderPlus,
  Hand,
  Keyboard,
  Maximize2,
  Move,
  MousePointer2,
  Redo2,
  Save,
  Square,
  Tag,
  Trash2,
  Undo2,
  Ungroup,
} from "lucide-react";
import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { tKey, type I18nKey } from "@/core/i18n";

export interface ShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A key cap. Letters, digits and arrows (`A`, `Z`, `1`, `↑`) are printed on
 * the keyboard and stay untranslated; named keys and the mouse click come
 * from the catalogs.
 */
type KeyCap = string | { name: I18nKey<"label" | "common"> };

const CTRL: KeyCap = { name: tKey("label:shortcuts.keys.ctrl") };
const SHIFT: KeyCap = { name: tKey("common:keys.shift") };
const DEL: KeyCap = { name: tKey("label:shortcuts.keys.del") };
const CLICK: KeyCap = { name: tKey("label:shortcuts.keys.click") };

interface ShortcutItem {
  icon?: ReactNode;
  label: I18nKey<"label" | "common">;
  keys: ReactNode;
}

interface ShortcutGroup {
  title: I18nKey<"label">;
  items: ShortcutItem[];
}

const Key = ({ children }: { children?: ReactNode }) => (
  <kbd className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-border bg-muted px-1.5 text-[11px] font-semibold text-foreground shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
    {children}
  </kbd>
);

const KeyCombo = ({ keys }: { keys: KeyCap[] }) => {
  const { t } = useTranslation(["label", "common"]);
  return (
    <div className="flex items-center gap-1">
      {keys.map((k, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <span className="text-[11px] text-muted-foreground">+</span>}
          <Key>{typeof k === "string" ? k : t(k.name)}</Key>
        </span>
      ))}
    </div>
  );
};

/** Shift plus any of the four arrows. */
const NudgeCombo = () => {
  const { t } = useTranslation(["label", "common"]);
  return (
    <div className="flex items-center gap-1">
      <Key>{t("common:keys.shift")}</Key>
      <span className="text-[11px] text-muted-foreground">+</span>
      <Key>↑</Key>
      <Key>↓</Key>
      <Key>←</Key>
      <Key>→</Key>
    </div>
  );
};

const groups: ShortcutGroup[] = [
  {
    title: tKey("label:shortcuts.groups.tools"),
    items: [
      {
        icon: <MousePointer2 />,
        label: tKey("label:shortcuts.items.select"),
        keys: <KeyCombo keys={["A"]} />,
      },
      {
        icon: <Square />,
        label: tKey("label:shortcuts.items.drawRectangle"),
        keys: <KeyCombo keys={["R"]} />,
      },
      {
        icon: <Square className="rotate-12" />,
        label: tKey("label:shortcuts.items.drawPolygon"),
        keys: <KeyCombo keys={["P"]} />,
      },
      {
        icon: <Hand />,
        label: tKey("label:shortcuts.items.pan"),
        keys: <KeyCombo keys={["M"]} />,
      },
      {
        icon: <Crosshair />,
        label: tKey("label:shortcuts.items.toggleCrosshair"),
        keys: <KeyCombo keys={["C"]} />,
      },
      {
        icon: <Maximize2 />,
        label: tKey("label:shortcuts.items.fit"),
        keys: <KeyCombo keys={["F"]} />,
      },
    ],
  },
  {
    title: tKey("label:shortcuts.groups.actions"),
    items: [
      {
        icon: <Save />,
        label: tKey("common:actions.save"),
        keys: <KeyCombo keys={["S"]} />,
      },
      {
        icon: <Undo2 />,
        label: tKey("label:shortcuts.items.undo"),
        keys: <KeyCombo keys={[CTRL, "Z"]} />,
      },
      {
        icon: <Redo2 />,
        label: tKey("label:shortcuts.items.redo"),
        keys: <KeyCombo keys={[CTRL, SHIFT, "Z"]} />,
      },
      {
        icon: <Trash2 />,
        label: tKey("label:shortcuts.items.deleteSelected"),
        keys: <KeyCombo keys={[DEL]} />,
      },
      {
        icon: <EyeOff />,
        label: tKey("label:shortcuts.items.hideAll"),
        keys: <KeyCombo keys={["H"]} />,
      },
    ],
  },
  {
    title: tKey("label:shortcuts.groups.editing"),
    items: [
      {
        icon: <Move />,
        label: tKey("label:shortcuts.items.nudge"),
        keys: <NudgeCombo />,
      },
    ],
  },
  {
    title: tKey("label:shortcuts.groups.selection"),
    items: [
      {
        icon: <MousePointer2 />,
        label: tKey("label:shortcuts.items.toggleSelection"),
        keys: <KeyCombo keys={[CTRL, CLICK]} />,
      },
      {
        icon: <MousePointer2 />,
        label: tKey("label:shortcuts.items.selectRange"),
        keys: <KeyCombo keys={[SHIFT, CLICK]} />,
      },
      {
        icon: <Copy />,
        label: tKey("label:shortcuts.items.copy"),
        keys: <KeyCombo keys={[CTRL, "C"]} />,
      },
      {
        icon: <ClipboardPaste />,
        label: tKey("label:shortcuts.items.paste"),
        keys: <KeyCombo keys={[CTRL, "V"]} />,
      },
      {
        icon: <FolderPlus />,
        label: tKey("label:shortcuts.items.group"),
        keys: <KeyCombo keys={[CTRL, "G"]} />,
      },
      {
        icon: <Ungroup />,
        label: tKey("label:shortcuts.items.ungroup"),
        keys: <KeyCombo keys={[CTRL, SHIFT, "G"]} />,
      },
    ],
  },
  {
    title: tKey("label:shortcuts.groups.navigation"),
    items: [
      {
        icon: <ArrowUp />,
        label: tKey("label:shortcuts.items.previousTask"),
        keys: <KeyCombo keys={["↑"]} />,
      },
      {
        icon: <ArrowDown />,
        label: tKey("label:shortcuts.items.nextTask"),
        keys: <KeyCombo keys={["↓"]} />,
      },
    ],
  },
  {
    title: tKey("label:shortcuts.groups.labels"),
    items: [
      {
        icon: <Tag />,
        label: tKey("label:shortcuts.items.pickLabel"),
        keys: <KeyCombo keys={["1"]} />,
      },
      {
        icon: <ArrowLeft />,
        label: tKey("label:shortcuts.items.previousLabel"),
        keys: <KeyCombo keys={["←"]} />,
      },
      {
        icon: <ArrowRight />,
        label: tKey("label:shortcuts.items.nextLabel"),
        keys: <KeyCombo keys={["→"]} />,
      },
    ],
  },
];

export const ShortcutsDialog = ({
  open,
  onOpenChange,
}: ShortcutsDialogProps) => {
  const { t } = useTranslation(["label", "common"]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-5">
              <Keyboard />
            </span>
            <div>
              <DialogTitle className="text-base">
                {t("shortcuts.title")}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {t("shortcuts.description")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-6 pt-2 sm:flex-row">
          {[groups.filter((_, i) => i % 2 === 0), groups.filter((_, i) => i % 2 === 1)].map(
            (col, ci) => (
              <div key={ci} className="flex flex-1 flex-col gap-5">
                {col.map((group) => (
                  <section key={group.title} className="flex flex-col gap-2">
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-muted-foreground">
                      {t(group.title)}
                    </p>
                    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-card">
                      {group.items.map((item, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0"
                        >
                          {item.icon && (
                            <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4">
                              {item.icon}
                            </span>
                          )}
                          <span className="flex-1 text-xs text-foreground">
                            {t(item.label)}
                          </span>
                          {item.keys}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ),
          )}
        </div>

        <p className="border-t border-border pt-3 text-[11px] text-muted-foreground">
          <Trans t={t} i18nKey="shortcuts.footer" components={{ key: <Key /> }} />
        </p>
      </DialogContent>
    </Dialog>
  );
};
